/** Putting an object on the map without breaking it.
 *
 * One rule governs every placement: after the footprint is stamped, the
 * object's entrance and every town entrance must still be in one walkable
 * component. A placement that fails that is rolled back, not repaired - the
 * generator would otherwise be free to wall a player into a corner and then
 * discover it two hundred objects later.
 *
 * The inner-loop test is an eight-connected flood over `cellEnterable`'s own
 * predicate; `computeReachable` - the kernel's Dijkstra, the same function the
 * game will use - runs at the end of generation over the finished map and has
 * the last word.
 */

import type { AdventureMapState, MapObjectDef, MapObjectKind, MapObjectState } from "@game/core";
import type { ContentRegistry, ObjectId } from "@game/core";
import { asObjectId, setOccupant } from "@game/core";
import type { Grid } from "./grid.js";
import { flood, idx, inside, terrainOf, walkable, xOf, yOf } from "./grid.js";
import { TERRAIN_BAN, WATER_ONLY, ROCK_DWELLING } from "./catalog.js";

export interface Placement {
  readonly def: MapObjectDef;
  /** Anchor cell: footprint offsets are added to it. */
  readonly x: number;
  readonly y: number;
  readonly cells: readonly number[];
  readonly entrances: readonly number[];
  /** Cells kept empty for this object's art. See `clearanceCells`. */
  readonly clearance: readonly number[];
}

export interface PlaceContext {
  readonly map: AdventureMapState;
  readonly content: ContentRegistry;
  /**
   * The level `map` *is*. `map` itself is always a one-level plane - the two
   * levels are generated separately and merged - so occupancy is stamped at
   * z = 0 and only the `MapObjectState` this produces carries the real level.
   */
  readonly z: number;
  /** Cells no object may cover: town aprons, road trunks, reserved corridors. */
  readonly reserved: Uint8Array;
  /** Entrance cells that must stay reachable - towns, and everything placed so far. */
  readonly anchors: number[];
  /** Scratch buffer for the flood, reused so placement does not allocate. */
  readonly scratch: Uint8Array;
}

export const footprintCells = (
  g: Grid, def: MapObjectDef, x: number, y: number,
): number[] | null => {
  const out: number[] = [];
  for (const [dx, dy] of def.footprint) {
    const cx = x + dx, cy = y + dy;
    if (!inside(g, cx, cy)) return null;
    out.push(idx(g, cx, cy));
  }
  return out;
};

export const entranceCells = (
  g: Grid, def: MapObjectDef, x: number, y: number,
): number[] | null => {
  const out: number[] = [];
  for (const [dx, dy] of def.entrances) {
    const cx = x + dx, cy = y + dy;
    if (!inside(g, cx, cy)) return null;
    out.push(idx(g, cx, cy));
  }
  return out;
};

/**
 * Scenery is allowed to grow into its neighbours; nothing else is.
 *
 * `obs_*` is a copse, a thicket, a boulder field - art authored to be stamped
 * next to itself until it reads as landscape, and a forest with a blank row
 * between every trunk reads as an orchard. Everything a hero can walk up to
 * has to stand on its own ground.
 */
const massesTogether = (def: MapObjectDef): boolean => def.kind === "obstacle";

/**
 * The cells a footprint has to keep empty for the sake of the ART, north and
 * south of it.
 *
 * A sprite is drawn from its ground line and is authored taller than the cells
 * it blocks: measured over the 348 shipped defs, 164 draw taller than their own
 * footprint and 25 of those by more than a whole row, up to `bank_red_tower`'s
 * four. Width is not the problem - every def in the set is exactly as wide as
 * its footprint - so the only arrangement that breaks is two objects whose
 * footprints touch north-south, where the southern one's art is drawn straight
 * through the middle of the northern one. That is how a wandering imp came to
 * be standing inside a learning stone, its head, arms and tail sticking out
 * either side of the slab, on the shipped adventure map.
 *
 * `MapObjectDef` carries no art height - it has a footprint and nothing else -
 * so the generator cannot ask how tall a thing is and instead keeps one row.
 * That covers every def that overhangs by up to a full row; the two dozen
 * towers and towns that reach further can still clip a neighbour two rows away,
 * and closing that needs an art height on the def.
 *
 * The row to the SOUTH is kept as well, and for the same reason read the other
 * way round: whatever stands there would be drawn in front of this object,
 * across its own feet. It is also the row a hero approaches the entrance from.
 */
function clearanceCells(g: Grid, cells: readonly number[]): number[] {
  const out: number[] = [];
  for (const i of cells) {
    const x = xOf(g, i), y = yOf(g, i);
    for (const ny of [y - 1, y + 1]) {
      if (!inside(g, x, ny)) continue;
      const j = idx(g, x, ny);
      if (cells.includes(j) || out.includes(j)) continue;
      out.push(j);
    }
  }
  return out;
}

/** Terrain rules: water objects want water, land objects want anything else. */
function terrainOk(m: AdventureMapState, def: MapObjectDef, cells: readonly number[]): boolean {
  const wantsWater = WATER_ONLY.has(def.id);
  const ban = TERRAIN_BAN[def.id] ?? [];
  let monsterWet: boolean | null = null;
  for (const i of cells) {
    const t = terrainOf(m, i);
    // The ridge-dressing mountains are the one exception: they stand on the
    // rock wall they illustrate (catalog.ts ROCK_DWELLING), and every cell of
    // their footprint must be rock. Requiring that here - rather than merely
    // allowing rock - is what stops a 2-wide mountain from sitting half on a
    // walkable cell at a wall's end, which is how a hero walked under the
    // overhung half of a peak.
    if (ROCK_DWELLING.has(def.id)) { if (t !== "rock") return false; continue; }
    if (t === "rock") return false;
    if (ban.includes(t)) return false;
    // Wandering stacks stand on land or water; the generator decides which by
    // which cells it offers. Mixed footprints (one toe in the sea) are
    // refused - that is a 2-wide dragon sitting on the beach and blocking
    // the only way to a shrine.
    if (def.kind === "monster") {
      const wet = t === "water";
      if (monsterWet === null) monsterWet = wet;
      else if (monsterWet !== wet) return false;
      continue;
    }
    if (wantsWater !== (t === "water")) return false;
  }
  return true;
}



/**
 * Stamp a footprint the way `layFootprint` does: the footprint blocks, an
 * entrance inside it does not. Keeping the two in step is what makes the
 * generator's reachability answer the kernel's reachability answer.
 */
function stamp(
  m: AdventureMapState, id: ObjectId, def: MapObjectDef,
  cells: readonly number[], entrances: readonly number[],
): void {
  for (const i of cells) {
    const blocks = def.blocksMovement && !entrances.includes(i);
    setOccupant(m, xOf(m, i), yOf(m, i), 0, id, blocks);
  }
}

function unstamp(m: AdventureMapState, cells: readonly number[]): void {
  for (const i of cells) setOccupant(m, xOf(m, i), yOf(m, i), 0, null);
}

export type Payload = Readonly<Record<string, number | string | boolean | null>>;

export interface PlaceResult {
  readonly state: MapObjectState;
  readonly placement: Placement;
}

export interface PlaceOptions {
  /**
   * Stand on a cell another object has reserved for its own art clearance.
   *
   * A reservation says "something nearby is drawn taller than its footprint and
   * this cell is where its overhang lands". That is a real conflict for a thing
   * placed in open ground, and the ridge dressing is not one: the mountain has
   * to stand on the rock cell or the wall has a hole in it, and there is no
   * other cell it could stand on instead.
   *
   * The caller carries the burden of checking that the overhang does not
   * actually clash - see `dressRidges`, which only claims a reserved cell when
   * the row its own sprite reaches into is empty.
   */
  readonly ignoreReserved?: boolean;
  /**
   * The caller guarantees this cell was IMPASSABLE until a moment ago, and is
   * only walkable now because the caller made it so in order to place something
   * blocking on it.
   *
   * That makes the connectivity flood below provably pointless: the walkable set
   * is the same before and after, so nothing can have been cut off. Without it
   * `plantWoodWalls` - which puts the ground back under a rock wall so a forest
   * can grow on it - paid for a whole-map flood per tree, twenty of the
   * forty-five seconds a 252x252 map took to generate.
   *
   * Only ever true where the caller restores the block on failure. `woodWalls.ts`
   * is the one caller, and it puts the rock back.
   */
  readonly groundWasBlocked?: boolean;
}

/**
 * Try to put `defId` with its anchor at `(x, y)`. Returns null when the cell is
 * unusable for any reason; the caller retries elsewhere rather than nudging the
 * object, because a nudge is how objects end up in a line.
 */
export function tryPlaceAt(
  ctx: PlaceContext, defId: string, x: number, y: number,
  id: ObjectId, payload: Readonly<Record<string, number | string | boolean | null>>,
  owner: string | null, opts: PlaceOptions = {},
): PlaceResult | null {
  const m = ctx.map;
  const g: Grid = { width: m.width, height: m.height };
  const def = ctx.content.mapObjects.get(defId);
  if (def === undefined) return null;

  const cells = footprintCells(g, def, x, y);
  if (cells === null) return null;
  const entrances = entranceCells(g, def, x, y);
  if (entrances === null) return null;

  for (const i of cells) {
    if (ctx.reserved[i] === 1 && opts.ignoreReserved !== true) return null;
    if (m.occupant[i] !== 0) return null;
    if (m.road[i] !== 0) return null;
    // A river is drawn under whatever stands on it, and a fort with a river
    // running out of its gate is the first thing a screenshot shows. Rock is
    // the exception: a ridge mountain over a river is a river that starts
    // under a mountain, which is where the classic's rivers start.
    if (m.river[i] !== 0 && walkable(m, i)) return null;
  }
  if (!terrainOk(m, def, cells)) return null;

  // The art clearance, checked both ways: this object's own overhang must not
  // land on a neighbour, and a neighbour placed later must not land on it. The
  // second half is what `ctx.reserved` carries once the placement sticks.
  const clearance = massesTogether(def) ? [] : clearanceCells(g, cells);
  for (const i of clearance) if (m.occupant[i] !== 0) return null;

  // An entrance on water is only reachable by boat; a land object with a wet
  // doorstep is a bug that only shows up when a hero tries to use it.
  const wantsWater = WATER_ONLY.has(def.id);
  if (!wantsWater) {
    for (const i of entrances) if (!walkable(m, i)) return null;
  }

  // Stamping a footprint whose cells were ALREADY unwalkable cannot change what
  // is connected to what, so the flood below has nothing to find. That is the
  // whole of the ridge dressing - a mountain stands on impassable rock - and on
  // a 144x144 map that is several hundred placements, each of which was
  // re-flooding forty thousand cells to be told the answer had not changed.
  // The flood below answers TWO questions, and only one of them is about what
  // this placement blocks:
  //
  //   1. does blocking these cells cut any anchor off from the others?
  //   2. is this object's own doorstep reachable from them at all?
  //
  // The test below settles (1) for the one case it can settle exactly: cells
  // that were ALREADY impassable, where the walkable set does not move and so
  // nothing can have been cut. It says nothing about (2), so an object that HAS
  // a doorstep always floods. Missing that was a real bug here - skipping the
  // flood for such an object let it be placed in a pocket nothing could walk to.
  //
  // A local cut-vertex test was tried here as well and taken out again. It is
  // sound about what a placement blocks and still changed the maps, because the
  // flood also refuses a placement whenever the anchors are ALREADY in two
  // pieces - which happens, six times on a 72x72 map - and no local test can
  // see that. Fifteen seconds on a 252x252 map is not worth a change to every
  // map that cannot be explained.
  const blocksNothingNew = (opts.groundWasBlocked === true)
    || cells.every((i) => !walkable(m, i));

  stamp(m, id, def, cells, entrances);

  const needsFlood = entrances.length > 0 || !blocksNothingNew;

  if (needsFlood && !wantsWater && (def.blocksMovement || entrances.length > 0)) {
    const root = ctx.anchors[0];
    if (root !== undefined) {
      flood(m, root, ctx.scratch);
      let ok = true;
      for (const a of ctx.anchors) if (ctx.scratch[a] !== 1) { ok = false; break; }
      if (ok) for (const e of entrances) if (ctx.scratch[e] !== 1) { ok = false; break; }
      if (!ok) { unstamp(m, cells); return null; }
    }
  }

  // The placement has stuck, so its clearance becomes a standing reservation:
  // scenery is exempt from the test above and would otherwise be free to plant
  // a trunk on the doorstep of the shrine that was placed first.
  for (const i of clearance) ctx.reserved[i] = 1;

  // Only land entrances become connectivity anchors: a boat's doorstep is
  // water, and a land flood would never reach it.
  if (!wantsWater) for (const e of entrances) ctx.anchors.push(e);

  const state: MapObjectState = {
    id,
    kind: def.kind as MapObjectKind,
    def: def.id,
    x, y, z: ctx.z,
    owner: owner as MapObjectState["owner"],
    payload,
    guard: null,
    visitedBy: [],
    removed: false,
  };
  return { state, placement: { def, x, y, cells, entrances, clearance } };
}

/**
 * The slice of `GenState` a placement writes to. Structural rather than the
 * `GenState` type itself because `state.ts` imports this module for
 * `PlaceContext`, and the import cannot run both ways.
 */
export interface PlaceTarget {
  readonly ctx: PlaceContext;
  readonly objects: MapObjectState[];
  readonly placements: Placement[];
  readonly idBase: number;
}

/**
 * Mint the next positional id, try the placement, and record it: the one body
 * every `place` variant in this package shares. Returns the new object's index
 * in `s.objects`, or -1 when `tryPlaceAt` refused the cell. The id rule -
 * `object:<idBase + n + 1>`, so the underground's list numbers on from the
 * surface's - is written here and nowhere else.
 */
export function placeCore(
  s: PlaceTarget, defId: string, x: number, y: number, payload: Payload,
  opts: PlaceOptions = {},
): number {
  const id = objectIdFor(s.idBase + s.objects.length + 1);
  const res = tryPlaceAt(s.ctx, defId, x, y, id, payload, null, opts);
  if (res === null) return -1;
  s.objects.push(res.state);
  s.placements.push(res.placement);
  return s.objects.length - 1;
}

export function removePlacement(m: AdventureMapState, p: Placement): void {
  unstamp(m, p.cells);
}

export const objectIdFor = (n: number): ObjectId => asObjectId(`object:${n}`);
